import { Operation as OperationV3_0 } from '@criteria/openapi/v3.0'
import { Operation as OperationV3_1 } from '@criteria/openapi/v3.1'
import { Inset, Pane, Scroll, Text } from '@criteria-labs/react'
import { CommandLineIcon } from '@heroicons/react/24/outline'
import {
  EmptyView,
  ErrorView,
  ExecuteButton,
  ExecutingView,
  RequestConfiguration,
  RequestRepresentation,
  ResponseView,
  StatusLine,
  useExecutions
} from 'criteria-requests-ui'
import { ReactNode } from 'react'
import { ConsoleTab } from '../console/ConsoleLayout'
import { APIExplorerOperationPicker } from './APIExplorerOperationPicker'
import { useAPIExplorer } from './APIExplorerProvider'

type Operation = OperationV3_1<never> | OperationV3_0<never>

function OperationRequestView({
  operationPicker,
  operation
}: {
  operationPicker: ReactNode
  operation: (Operation & { path: `/${string}`; method: string; operationRef: string }) | null
}) {
  const [executions, execute, cancel] = useExecutions()

  const currentExecution = executions.length > 0 ? executions[executions.length - 1] : null
  const executing = currentExecution?.state === 'executing'
  const response = currentExecution?.state === 'executed' ? currentExecution.response : null
  const time = currentExecution?.state === 'executed' ? currentExecution.time : null
  const error = currentExecution?.state === 'failed' ? currentExecution.error : null

  return (
    <div className="grid h-full grid-cols-1 sm:grid-cols-3">
      <div className="flex flex-col overflow-hidden border-zinc-950/10 sm:border-r dark:border-white/10">
        <div className="flex h-14 w-full shrink-0 items-center gap-2 border-b border-zinc-950/10 bg-zinc-50 p-2 dark:border-white/10 dark:bg-zinc-900">
          {operationPicker}
          <ExecuteButton
            onExecute={() => {
              execute()
            }}
            onCancel={() => {
              cancel()
            }}
            executing={executing}
          />
        </div>
        <div className="min-h-0">
          <Pane background="secondary">
            <Scroll>
              {operation && (
                <>
                  <RequestConfiguration />
                  <RequestRepresentation />
                </>
              )}
            </Scroll>
          </Pane>
        </div>
      </div>
      <div className="flex flex-col overflow-hidden sm:col-span-2">
        {!currentExecution && <EmptyView />}
        {executing && <ExecutingView />}
        {response && (
          <>
            <div className="flex h-14 w-full shrink-0 gap-2 border-b border-zinc-950/10 bg-zinc-50 p-3 dark:border-white/10 dark:bg-zinc-900">
              <StatusLine response={response} time={time ?? 0} />
            </div>
            <Pane className="min-h-0 flex-1" background="secondary">
              <ResponseView response={response} time={time ?? 0} />
            </Pane>
          </>
        )}
        {error && (
          <Inset>
            <ErrorView error={error} />
          </Inset>
        )}
      </div>
    </div>
  )
}

export function APIExplorerPanelHeader({ selected, onClick }: { selected: boolean; onClick?: () => void }) {
  return (
    <ConsoleTab selected={selected} onClick={onClick}>
      <span className="flex items-center text-sm/6 font-medium text-zinc-950 dark:text-white">
        <CommandLineIcon className="mr-2 size-5" />
        API Explorer
      </span>
    </ConsoleTab>
  )
}

export function APIExplorerPanelBody() {
  const { namespace, openAPI, server, setServer, operation, setOperation } = useAPIExplorer()
  return (
    <>
      {!openAPI && (
        <Pane>
          <Inset className="flex h-full w-full items-center justify-center">
            <Text>Select an OpenAPI document to begin.</Text>
          </Inset>
        </Pane>
      )}
      {openAPI && (
        <OperationRequestView
          operationPicker={<APIExplorerOperationPicker openAPI={openAPI} value={operation} onChange={setOperation} />}
          operation={operation}
        />
      )}
    </>
  )
}
